import type {
  DsrCreate,
  DsrExport,
  DsrRequest,
  DsrRequestDetail,
  LegalHold,
  LegalHoldInput,
  Page,
  PrivacyRun,
  RetentionRule,
  RetentionRuleInput,
} from '@sahab/contracts'
import {
  keepPreviousData,
  type QueryClient,
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { api } from '../../lib/api'
import { type DsrListParams, DUE_SOON_DAYS } from './search'

/** Privacy (R0b PR7/PR8): retention rules, legal holds, retention runs and the data-subject requests. */
export const privacyKeys = {
  all: ['privacy'] as const,
  rules: () => [...privacyKeys.all, 'rules'] as const,
  runs: () => [...privacyKeys.all, 'runs'] as const,
  holds: () => [...privacyKeys.all, 'holds'] as const,
  dsr: () => [...privacyKeys.all, 'dsr'] as const,
  dsrList: (query: DsrListParams) => [...privacyKeys.dsr(), 'list', query] as const,
  dsrCounts: () => [...privacyKeys.dsr(), 'counts'] as const,
  dsrDetail: (id: string) => [...privacyKeys.dsr(), 'detail', id] as const,
}

// ── settings › privacy ─────────────────────────────────────────────────────

export const retentionRulesQueryOptions = queryOptions({
  queryKey: privacyKeys.rules(),
  queryFn: ({ signal }) => api.get<{ items: RetentionRule[] }>('/privacy/retention-rules', { signal }),
})

/** The latest retention runs (one per class per day): the section shows the last one of each class. */
export const privacyRunsQueryOptions = queryOptions({
  queryKey: privacyKeys.runs(),
  queryFn: ({ signal }) =>
    api.get<Page<PrivacyRun>>('/privacy/runs', { signal, query: { page: 1, pageSize: 20 } }),
})

export function useSaveRetentionRule() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: RetentionRuleInput) => api.put<RetentionRule>('/privacy/retention-rules', input),
    onSuccess: () => void client.invalidateQueries({ queryKey: privacyKeys.rules() }),
  })
}

// ── legal holds ──────────────────────────────────────────────────────────────

export type HoldView = 'active' | 'lifted'

export function legalHoldsQueryOptions(view: HoldView) {
  return queryOptions({
    queryKey: [...privacyKeys.holds(), view] as const,
    queryFn: ({ signal }) =>
      api.get<Page<LegalHold>>('/privacy/legal-holds', {
        signal,
        query: {
          active: view === 'active' ? 'true' : 'false',
          sort: view === 'active' ? '-placedAt' : '-liftedAt',
          page: 1,
          pageSize: 200,
        },
      }),
    placeholderData: keepPreviousData,
  })
}

const settleHolds = (client: QueryClient) => void client.invalidateQueries({ queryKey: privacyKeys.holds() })

export function usePlaceHold() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: LegalHoldInput) => api.post<LegalHold>('/privacy/legal-holds', input),
    onSuccess: () => settleHolds(client),
  })
}

export function useLiftHold() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.post<LegalHold>(`/privacy/legal-holds/${id}/lift`, { reason }),
    onSuccess: () => settleHolds(client),
  })
}

// ── data-subject requests ────────────────────────────────────────────────────

export function dsrListQueryOptions(query: DsrListParams) {
  return queryOptions({
    queryKey: privacyKeys.dsrList(query),
    queryFn: ({ signal }) =>
      api.get<Page<DsrRequest>>('/privacy/dsr', {
        signal,
        query: {
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort,
          q: query.q,
          kind: query.kind,
          ...(query.view === 'open' ? { open: 'true' } : {}),
          ...(query.view === 'dueSoon' ? { dueWithin: DUE_SOON_DAYS } : {}),
          ...(query.view === 'overdue' ? { overdue: 'true' } : {}),
          ...(query.view === 'closed' ? { open: 'false' } : {}),
        },
      }),
    placeholderData: keepPreviousData,
  })
}

/** The counts on the queue's tabs (one request each, page size 1). */
export const dsrCountsQueryOptions = queryOptions({
  queryKey: privacyKeys.dsrCounts(),
  queryFn: async ({ signal }) => {
    const total = (query: Record<string, string | number>) =>
      api
        .get<Page<DsrRequest>>('/privacy/dsr', { signal, query: { page: 1, pageSize: 1, ...query } })
        .then((p) => p.total)
    const [open, dueSoon, overdue] = await Promise.all([
      total({ open: 'true' }),
      total({ dueWithin: DUE_SOON_DAYS }),
      total({ overdue: 'true' }),
    ])
    return { open, dueSoon, overdue }
  },
})

export function dsrQueryOptions(id: string) {
  return queryOptions({
    queryKey: privacyKeys.dsrDetail(id),
    queryFn: ({ signal }) => api.get<DsrRequestDetail>(`/privacy/dsr/${id}`, { signal }),
  })
}

function settleDsr(client: QueryClient, request: DsrRequestDetail): void {
  client.setQueryData(privacyKeys.dsrDetail(request.id), request)
  void client.invalidateQueries({ queryKey: [...privacyKeys.dsr(), 'list'] })
  void client.invalidateQueries({ queryKey: privacyKeys.dsrCounts() })
}

export function useCreateDsr() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: DsrCreate) => api.post<DsrRequestDetail>('/privacy/dsr', input),
    onSuccess: (request) => settleDsr(client, request),
  })
}

export type DsrCommand =
  | { action: 'verify'; subjectType: 'customer' | 'user'; subjectId: string }
  | { action: 'start' }
  | { action: 'complete' }
  | { action: 'refuse'; reason: string }

export function useDsrCommand(id: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ command, version }: { command: DsrCommand; version: number }) => {
      const { action, ...body } = command
      return api.post<DsrRequestDetail>(`/privacy/dsr/${id}/${action}`, { ...body, version })
    },
    onSuccess: (request) => settleDsr(client, request),
    // A refused erasure (DSR_SUBJECT_REFERENCED) commits the refusal: show the request as it is now.
    onError: () => {
      void client.invalidateQueries({ queryKey: privacyKeys.dsrDetail(id) })
      void client.invalidateQueries({ queryKey: [...privacyKeys.dsr(), 'list'] })
      // …and the queue's tab counts (Open, Due in 7 days, Overdue), which still count it as open.
      void client.invalidateQueries({ queryKey: privacyKeys.dsrCounts() })
    },
  })
}

export function fetchDsrExport(id: string): Promise<DsrExport> {
  return api.get<DsrExport>(`/privacy/dsr/${id}/export`)
}
